import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useVerifyEmail, useResendVerificationEmail, useCurrentUser } from "../../../hooks/useAPI";
import {
  AuthShell,
  AuthInput,
  AuthButton,
  TokenStatePage,
  type TokenPageStatus,
} from "./_primitives";

const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const verifyEmail = useVerifyEmail();
  const resend = useResendVerificationEmail();
  const { data: currentUser } = useCurrentUser();

  const [status, setStatus] = useState<TokenPageStatus>(token ? "loading" : "no-token");
  const [resendEmail, setResendEmail] = useState("");
  const [resendError, setResendError] = useState("");

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    if (typeof window !== "undefined") {
      window.history.replaceState({}, "", window.location.pathname);
    }
    verifyEmail.mutate(
      { token },
      {
        onSuccess: () => { if (mounted) setStatus("success"); },
        onError: (err) => {
          if (!mounted) return;
          const code = (err as { code?: string } | undefined)?.code || "";
          const message = err instanceof Error ? err.message : "";
          const lowered = (code + " " + message).toLowerCase();
          if (
            code === "ALREADY_VERIFIED" ||
            code === "EMAIL_ALREADY_VERIFIED" ||
            lowered.includes("already verified")
          ) {
            setStatus("consumed");
          } else if (lowered.includes("expired")) {
            setStatus("expired");
          } else {
            setStatus("error");
          }
        },
      },
    );
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleResend = () => {
    setResendError("");
    const authedEmail = currentUser?.email;
    const candidate = authedEmail || resendEmail.trim();
    if (!candidate || !isValidEmail(candidate)) {
      setResendError(t("verifyEmail.errors.invalidEmail"));
      return;
    }
    resend.mutate(authedEmail ? undefined : candidate, {
      onSuccess: () => {
        toast.success(t("verifyEmail.toasts.resent"), { description: t("verifyEmail.toasts.resentDescription", { email: candidate }) });
        setStatus("no-token");
      },
      onError: () => {
        toast.error(t("verifyEmail.toasts.resentFailedTitle"), { description: t("verifyEmail.toasts.resentFailedDescription") });
      },
    });
  };

  const ResendForm = () => {
    const authedEmail = currentUser?.email;
    return (
      <form
        onSubmit={(e) => { e.preventDefault(); handleResend(); }}
        className="space-y-3 w-full"
        noValidate
      >
        {!authedEmail && (
          <AuthInput
            label={t("verifyEmail.resendEmailLabel")}
            type="email"
            name="resend-email"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="off"
            spellCheck={false}
            disabled={resend.isPending}
            error={resendError || undefined}
          />
        )}
        <AuthButton
          type="submit"
          variant="primary"
          fullWidth
          loading={resend.isPending}
          loadingLabel={t("verifyEmail.resendSubmitLoading")}
        >
          {t("verifyEmail.resendSubmit")}
        </AuthButton>
      </form>
    );
  };

  return (
    <AuthShell pageTitle={t("verifyEmail.pageTitle")} pageDescription={t("verifyEmail.pageDescription")}>
      <TokenStatePage
        status={status}
        loadingTitle={t("verifyEmail.loadingTitle")}
        loadingBody={t("verifyEmail.loadingBody")}
        successTitle={t("verifyEmail.successTitle")}
        successBody={t("verifyEmail.successBody")}
        successAction={
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {t("verifyEmail.successAction")}
          </button>
        }
        consumedTitle={t("verifyEmail.consumedTitle")}
        consumedBody={t("verifyEmail.consumedBody")}
        consumedAction={
          <button
            type="button"
            onClick={() => navigate(currentUser ? "/dashboard" : "/login")}
            className="adp-btn adp-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {currentUser ? t("verifyEmail.consumedActionSignedIn") : t("verifyEmail.consumedActionSignedOut")}
          </button>
        }
        expiredTitle={t("verifyEmail.expiredTitle")}
        expiredBody={t("verifyEmail.expiredBody")}
        expiredAction={<ResendForm />}
        errorTitle={t("verifyEmail.errorTitle")}
        errorBody={t("verifyEmail.errorBody")}
        errorAction={<ResendForm />}
        noTokenTitle={t("verifyEmail.noTokenTitle")}
        noTokenBody={t("verifyEmail.noTokenBody")}
        noTokenAction={<ResendForm />}
      />

      <div className="pt-4 border-t border-[var(--border)] mt-6 text-center">
        <Link
          to={currentUser ? "/dashboard" : "/login"}
          className="auth-tertiary"
        >
          {currentUser ? t("verifyEmail.backLinkSignedIn") : t("verifyEmail.backLinkSignedOut")}
        </Link>
      </div>
    </AuthShell>
  );
}
